import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

/**
 * Obtiene todos los items de inventario con filtros opcionales
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales (name, sku, subcategory_id, low_stock)
 * @returns {Promise<Object>} - Respuesta con los items de inventario
 */
export const getInventoryItems = async (clientId, filters = {}) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('inventoryLoading', { detail: true });
    window.dispatchEvent(event);

    // Construir query params si hay filtros
    let queryParams = '';
    if (Object.keys(filters).length > 0) {
      queryParams = '?' + new URLSearchParams(filters).toString();
    }

    const response = await axiosWithAuth.get(
      `${API_URL}/workflows/p2p/${clientId}/inventory${queryParams}`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('inventoryLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('inventoryLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener items de inventario:", error.response?.data || error);
    throw error;
  }
};

/**
 * Obtiene un item de inventario específico por ID
 * @param {string} clientId - ID del cliente
 * @param {string} itemId - ID del item de inventario
 * @returns {Promise<Object>} - Respuesta con el item de inventario
 */
export const getInventoryItemById = async (clientId, itemId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('inventoryItemLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axiosWithAuth.get(
      `${API_URL}/workflows/p2p/${clientId}/inventory/${itemId}`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('inventoryItemLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('inventoryItemLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener item de inventario:", error.response?.data || error);
    throw error;
  }
};

/**
 * Crea un nuevo item de inventario
 * @param {string} clientId - ID del cliente
 * @param {Object} itemData - Datos del item de inventario
 * @returns {Promise<Object>} - Respuesta con el item creado
 */
export const createInventoryItem = async (clientId, itemData) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('inventorySaving', { detail: true });
    window.dispatchEvent(event);

    const response = await axiosWithAuth.post(
      `${API_URL}/workflows/p2p/${clientId}/inventory`,
      itemData
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('inventorySaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('inventorySaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al crear item de inventario:", error.response?.data || error);
    throw error;
  }
};

/**
 * Actualiza un item de inventario existente
 * @param {string} clientId - ID del cliente
 * @param {string} itemId - ID del item de inventario
 * @param {Object} itemData - Datos actualizados del item
 * @returns {Promise<Object>} - Respuesta con el item actualizado
 */
export const updateInventoryItem = async (clientId, itemId, itemData) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('inventoryUpdating', { detail: true });
    window.dispatchEvent(event);

    const response = await axiosWithAuth.put(
      `${API_URL}/workflows/p2p/${clientId}/inventory/${itemId}`,
      itemData
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('inventoryUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('inventoryUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al actualizar item de inventario:", error.response?.data || error);
    throw error;
  }
};

/**
 * Elimina un item de inventario
 * @param {string} clientId - ID del cliente
 * @param {string} itemId - ID del item de inventario
 * @returns {Promise<Object>} - Respuesta con la confirmación de eliminación
 */
export const deleteInventoryItem = async (clientId, itemId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('inventoryDeleting', { detail: true });
    window.dispatchEvent(event);

    const response = await axiosWithAuth.delete(
      `${API_URL}/workflows/p2p/${clientId}/inventory/${itemId}`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('inventoryDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('inventoryDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al eliminar item de inventario:", error.response?.data || error);
    throw error;
  }
};

/**
 * Obtiene la configuración de generación de SKU para un cliente
 * @param {string} clientId - ID del cliente
 * @returns {Promise<Object>} - Respuesta con la configuración de SKU
 */
export const getSKUConfig = async (clientId) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('skuConfigLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axiosWithAuth.get(
      `${API_URL}/workflows/p2p/${clientId}/inventory/sku-config`
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('skuConfigLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('skuConfigLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener configuración de SKU:", error.response?.data || error);
    throw error;
  }
};

/**
 * Actualiza la configuración de generación de SKU para un cliente
 * @param {string} clientId - ID del cliente
 * @param {Object} configData - Datos de configuración de SKU
 * @returns {Promise<Object>} - Respuesta con la configuración actualizada
 */
export const updateSKUConfig = async (clientId, configData) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('skuConfigUpdating', { detail: true });
    window.dispatchEvent(event);

    const response = await axiosWithAuth.put(
      `${API_URL}/workflows/p2p/${clientId}/inventory/sku-config`,
      configData
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('skuConfigUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('skuConfigUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al actualizar configuración de SKU:", error.response?.data || error);
    throw error;
  }
};

/**
 * Genera un nuevo SKU para un item de inventario
 * @param {string} clientId - ID del cliente
 * @param {Object} itemData - Datos del item para generar el SKU
 * @returns {Promise<Object>} - Respuesta con el SKU generado
 */
export const generateSKU = async (clientId, itemData) => {
  try {
    // Enviar evento para mostrar indicador de carga
    const event = new CustomEvent('skuGenerating', { detail: true });
    window.dispatchEvent(event);

    const response = await axiosWithAuth.post(
      `${API_URL}/workflows/p2p/${clientId}/inventory/generate-sku`,
      itemData
    );
    
    // Enviar evento para ocultar indicador de carga
    const endEvent = new CustomEvent('skuGenerating', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar indicador de carga en caso de error
    const endEvent = new CustomEvent('skuGenerating', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al generar SKU:", error.response?.data || error);
    throw error;
  }
};

/**
 * Valida items de inventario para carga masiva
 * @param {string} clientId - ID del cliente
 * @param {Array} items - Array de items a validar
 * @returns {Promise<Object>} - Respuesta con items válidos, inválidos y errores
 */
export const validateBulkInventoryItems = async (clientId, items) => {
  try {
    const response = await axiosWithAuth.post(
      `${API_URL}/workflows/p2p/${clientId}/inventory/bulk/validate`,
      { items }
    );
    return response.data;
  } catch (error) {
    console.error("Error al validar carga masiva:", error.response?.data || error);
    throw error;
  }
};

/**
 * Realiza la carga masiva de items de inventario
 * @param {string} clientId - ID del cliente
 * @param {Array} items - Array de items a cargar
 * @returns {Promise<Object>} - Respuesta con items creados y errores
 */
export const createBulkInventoryItems = async (clientId, items) => {
  try {
    const response = await axiosWithAuth.post(
      `${API_URL}/workflows/p2p/${clientId}/inventory/bulk/create`,
      { items }
    );
    return response.data;
  } catch (error) {
    console.error("Error al crear carga masiva:", error.response?.data || error);
    throw error;
  }
};

